import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { gql, useMutation, useQuery } from "urql";
import { Alert, AlertVariant } from "../../../../../components/src/Alert";
import { Ban, RotateCcw, Loader2 } from "lucide-react";
import { Link, useLocation, useParams, useSearchParams } from "wouter";

import { OP_PAYMENTS_QUERY } from "../../../../../graphql/src/oppayment/queries";
import { YesNoModal } from "../../../../../components/src/YesNoModal";
import { IPaymentDataRow, PaymentsGrid } from "./OpenPaymentsPaymentsGrid";
import { OpPayment } from "../../../types.generated";
import { ICommandBarMenuItem } from "../../../../../components/src/common/CommandBar";

import { Panel } from "../../../../../components/src/common/Panel";
import { PaymentForm } from "./OpenPaymentsPaymentForm";
import { PaymentSignPanel } from "./OpenPaymentsPaymentSignPanel";
import { CreateOpPaymentsButton } from "./CreateOpPaymentsButton";
import { DateForm } from "../../../../../components/src/dateForm";
import { useQueryState } from "../../../../../components/src/use-query-state";
import { useAlertTimeOut } from "../../../../../components/src/common/Utils";
import { OPItemContext } from "../../../contexts/OPItemContext";

const OP_DELETE_PAYMENTS = gql`
    mutation opDeletePayments($idIn: [GraphQLObjectId!]!) {
        opDeletePayments(idIn: $idIn) {
            _id
        }
    }
`;

const OP_UPDATE_PAYMENTS = gql`
    mutation opUpdatePayments($idIn: [GraphQLObjectId!]!) {
        opUpdatePayments(idIn: $idIn) {
            _id
        }
    }
`;

const CREATE_OP_SIGNING_BASKET_TOKEN = gql`
    mutation opCreateSigningBasketToken($code: String!, $signingBasketId: String!, $signingBasketAuthId: String!, $legalNumber: String!) {
        opCreateSigningBasketToken(
            code: $code
            signingBasketId: $signingBasketId
            signingBasketAuthId: $signingBasketAuthId
            legalNumber: $legalNumber
        ) {
            updateUserId
            updateUserInfo {
                name
            }
        }
    }
`;

export function PaymentsPage(): React.ReactElement {
    const { id } = useParams<{ id: string }>();
    const [queryArgs, setQueryArgs] = useSearchParams();

    const [startDate] = useQueryState("startDate", new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));

    const { opItem: opSigningBasket, setOPItem: setOPSigningBasket } = useContext(OPItemContext);

    const [_____, opCreateSigningBasketToken] = useMutation(CREATE_OP_SIGNING_BASKET_TOKEN);

    const [{ fetching: fetchingPayments, data: dataPayments, error: errorPayments }, refetch] = useQuery({
        query: OP_PAYMENTS_QUERY,
        variables: {
            filter: { startDate: startDate }
        }
    });

    const [__, opDeletePayments] = useMutation(OP_DELETE_PAYMENTS);
    const [___, opUpdatePayments] = useMutation(OP_UPDATE_PAYMENTS);

    const [spinner, setSpinner] = useState(false);
    const [selected, setSelected] = useState(null);
    const hasSelected = selected && selected.length > 0;
    const [showSignPanel, setShowSignPanel] = useState(false);

    const [, navigate] = useLocation();

    const selectedChange = (e: any[]) => {
        setSelected(e);
    };

    const [modal, setModal] = useState({ showModal: false, payload: null });

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    // Clear redirect-related query args whenever they're present (after redirect from bank)
    /*useEffect(() => {
        if (queryArgs.get("code") || queryArgs.get("scope") || queryArgs.get("state")) {
            const newQueryArgs = { ...Object.fromEntries(queryArgs) };
            delete newQueryArgs.code;
            delete newQueryArgs.scope;
            delete newQueryArgs.state;
            setQueryArgs(newQueryArgs, { replace: true });
        }
    }, [queryArgs, setQueryArgs]);*/

    const tokenCreated = useRef(false);

    useEffect(() => {
        const signingBasketId = opSigningBasket?.itemId;
        const signingBasketAuthId = opSigningBasket?.itemAuthId;
        const code = queryArgs.get("code");

        console.log("🔍 Effect Triggered");
        console.log("  tokenCreated.current:", tokenCreated.current);
        console.log("  signingBasketId:", signingBasketId);
        console.log("  signingBasketAuthId:", signingBasketAuthId);
        console.log("  code:", code);

        // Step-by-step conditional breakdown for debugging
        if (tokenCreated.current) {
            console.log("⛔ Skipping: Token already created.");
            return;
        }

        if (!signingBasketId) {
            console.log("⛔ Skipping: Missing signingBasketId.");
            return;
        }

        if (!signingBasketAuthId) {
            console.log("⛔ Skipping: Missing signingBasketAuthId.");
            return;
        }

        if (!code) {
            console.log("⛔ Skipping: Missing code from query args.");
            return;
        }

        // ✅ All validations passed — set tokenCreated and run
        tokenCreated.current = true;
        console.log("✅ All inputs present — creating token");

        opCreateSigningBasketToken({ signingBasketId, signingBasketAuthId, code, legalNumber: opSigningBasket.legalNumber })
            .then((result) => {
                if ("error" in result && result.error) {
                    console.error("❌ Token creation error:", result.error);
                    setAlert({ color: "danger", visible: true, message: result.error.toString() });
                }
            })
            .catch((error) => {
                console.error("❌ Token creation exception:", error);
                setAlert({ color: "danger", visible: true, message: error.toString() });
            })
            .finally(() => {
                console.log("🔚 Token creation complete — cleaning up");
                setOPSigningBasket({ itemId: null, itemAuthId: null, bic: "", clientId: "", legalNumber: "" });
                refetch({ requestPolicy: "network-only" });
                setQueryArgs({}, { replace: true });
            });
    }, [opSigningBasket, queryArgs, opCreateSigningBasketToken, setOPSigningBasket, refetch, setQueryArgs]);

    useAlertTimeOut(alert, setAlert, 5);

    const items: IPaymentDataRow[] = useMemo(() => {
        let items: IPaymentDataRow[] = [];
        if (dataPayments && dataPayments.opPayments) {
            items = dataPayments.opPayments.map((payment: OpPayment) => {
                return {
                    _id: payment._id,
                    clientId: payment.clientId,
                    clientName: payment.client ? payment.client.name : null,
                    instructedAmount: payment.instructedAmount ? payment.instructedAmount.amount : null,
                    createTimestamp: payment.createTimestamp,
                    updateTimestamp: payment.updateTimestamp,
                    requestedExecutionDate: payment.requestedExecutionDate,
                    transactionStatus: payment.transactionStatus,
                    remittanceInformationUnstructured: payment.remittanceInformationUnstructured,
                    updatedByUserName: payment.updateUserInfo ? payment.updateUserInfo.name : null,
                    debtorAccountIban: payment.debtorAccount ? payment.debtorAccount.iban : null,
                    creditorAccountIban: payment.creditorAccount ? payment.creditorAccount.iban : null,
                    signingBasketId: payment.signingBasketId
                };
            });
        }
        return items;
    }, [dataPayments]);

    if (fetchingPayments) return <p>Loading</p>;
    if (errorPayments) return <pre>errorOPPayments: {JSON.stringify(errorPayments, null, 2)}</pre>;

    const refetchDeletePayments = async (input: string[], type: "refetch" | "delete") => {
        if (type === "refetch") {
            console.log("refetching payments", input);
            await opUpdatePayments({ idIn: input })
                .then((result) => {
                    if (result.error) {
                        setSpinner(false);
                        console.error(result.error.toString());
                        setAlert({
                            color: "danger",
                            visible: true,
                            message: result.error.toString()
                        });
                    } else {
                        setSpinner(false);
                        refetch();
                    }
                })
                .catch((error) => {
                    setSpinner(false);
                    console.error(error);
                    setAlert({
                        color: "danger",
                        visible: true,
                        message: error.toString()
                    });
                });
        } else if (type === "delete") {
            await opDeletePayments({ idIn: input }).then((result) => {
                if (result.error) {
                    setSpinner(false);
                    console.error(result.error.toString());
                    setAlert({
                        color: "danger",
                        visible: true,
                        message: result.error.toString()
                    });
                } else {
                    setSpinner(false);
                    refetch();
                }
            });
        } else {
            console.error("Unknown update type " + type);
        }
    };

    const commands: ICommandBarMenuItem[] = [
        {
            itemKey: "newItem",
            text: "More options",
            subMenuProps: {
                items: [
                    {
                        itemKey: "refetch",
                        text: "Refetch",
                        icon: <RotateCcw />,
                        onClick: () => {
                            setSpinner(true);
                            const input = selected.map((payment: IPaymentDataRow) => {
                                return payment._id;
                            });
                            refetchDeletePayments(input, "refetch");
                        }
                    },
                    {
                        itemKey: "delete",
                        text: "Delete",
                        icon: <Ban />,
                        onClick: () => {
                            setSpinner(true);
                            const input = selected.map((payment: IPaymentDataRow) => {
                                return payment._id;
                            });
                            refetchDeletePayments(input, "delete");
                        }
                    }
                ]
            }
        },
        {
            itemKey: "sign",
            text: "Sign",
            onClick: () => {
                setShowSignPanel(true);
            }
        }
    ];

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-2/12 px-2 mb-4">
                    <Link to="/openpayments/consents">Consents page</Link>
                </div>
                <div className="w-full md:w-4/12 md:ml-auto px-2 mb-4 flex justify-end">
                    <Link to={"/openpayments/payments/new"}>New payment</Link>
                </div>
            </div>

            <div>
                {modal.showModal ? (
                    <YesNoModal
                        warningText={"Are you sure you want to delete payments with ids " + modal.payload.idIn + "? It cannot be undone."}
                        yesText={"Delete"}
                        modal={{
                            showModal: modal.showModal,
                            payload: modal.payload
                        }}
                        setModal={setModal}
                        onYes={() => {
                            opDeletePayments({ idIn: modal.payload.idIn })
                                .then((result) => {
                                    if ("error" in result && result.error) {
                                        setSpinner(false);
                                        console.error("❌ Delete payments error:", result.error);
                                        setAlert({
                                            color: "danger",
                                            visible: true,
                                            message: result.error.toString()
                                        });
                                    }
                                })
                                .catch((error) => {
                                    setSpinner(false);
                                    console.error("❌ Delete payments exception:", error);
                                    setAlert({ color: "danger", visible: true, message: error.toString() });
                                })
                                .finally(() => {
                                    console.log("🔚 Delete payments complete — refetching");
                                    refetch({ requestPolicy: "network-only" });
                                });
                        }}
                    />
                ) : null}
            </div>

            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-6/12 px-2 mb-4 flex mt-3">
                    <DateForm defaultDateString={startDate} dateName={"startDate"}></DateForm>
                </div>
            </div>

            <CreateOpPaymentsButton />

            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-6/12 px-2 mb-4 flex mt-3">
                    <p>
                        Payments should be refetched before trying to sign. <br></br>Payments can only be signed/refetched/deleted by the
                        person who created them (exception is counter signature, not available atm).
                    </p>
                </div>
            </div>

            <button
                className="btn btn-secondary mt-2"
                onClick={() => {
                    setSpinner(true);
                    refetchDeletePayments(
                        items.map((payment: IPaymentDataRow) => payment._id),
                        "refetch"
                    );
                }}
            >
                Refetch all
            </button>

            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    {alert.visible ? (
                        <Alert variant={alert.color} onClose={onDismissAlert} dismissible>
                            {alert.message}
                        </Alert>
                    ) : null}
                </div>
            </div>

            {spinner ? (
                <div className="flex items-center gap-2 my-3">
                    <Loader2 size={20} className="animate-spin" />
                    <span>Updating...</span>
                </div>
            ) : null}
            <div className="flex flex-wrap -mx-2 mt-4">
                <div className="w-full px-2 mb-4">
                    <PaymentsGrid
                        items={items}
                        onSelectedChange={selectedChange}
                        commands={hasSelected ? commands : []}
                        showExcelButton={false}
                    />
                </div>
            </div>

            {id ? (
                <Panel
                    isOpen={id != null}
                    onDismiss={() => {
                        navigate(`/openpayments/payments`);
                    }}
                    title={"Payment"}
                >
                    <PaymentForm />
                </Panel>
            ) : null}

            <PaymentSignPanel
                isOpen={showSignPanel}
                onDismiss={() => setShowSignPanel(false)}
                paymentIds={selected ? selected.map((payment: IPaymentDataRow) => payment._id) : []}
                refetch={refetchDeletePayments}
            />
        </div>
    );
}
